import { useMemo, useState } from "react";
import "./AdminHelp.css";

export default function AdminHelp() {
  // ----- FAQ data (can be swapped with API data later) -----
  const allFaqs = useMemo(
    () => [
      {
        id: "f1",
        q: "How do I manage company accounts in the admin panel?",
        a: "Navigate to the Companies section from the admin menu. You can view all companies, add new ones, edit details, and monitor their activity status.",
      },
      {
        id: "f2",
        q: "How can I view user diagnostics and progress?",
        a: "The Analytics page provides comprehensive insights into user diagnostics, completion rates, and learning development plan progress across all companies.",
      },
      {
        id: "f3",
        q: "What is the Communities of Practice section?",
        a: "Communities of Practice allows you to manage events, track attendance, and facilitate peer learning sessions. You can create new events, manage RSVPs, and monitor engagement.",
      },
      {
        id: "f4",
        q: "How do I add new users to the platform?",
        a: "Go to the Users section in the admin menu. Click 'Add User' and fill in the required information including name, email, role, and company assignment.",
      },
      {
        id: "f5",
        q: "Can I export analytics data?",
        a: "Yes. Most analytics pages include export options. You can download data as CSV or PDF reports from the respective page actions.",
      },
      {
        id: "f6",
        q: "How do I manage admin permissions?",
        a: "Admin permissions are configured in the Settings page. You can assign different access levels to admin users, controlling what sections and actions they can perform.",
      },
      {
        id: "f7",
        q: "What is the difference between admin and company users?",
        a: "Admin users have platform-wide access to manage all companies, users, and settings. Company users only have access to their organization's data and learning resources.",
      },
      {
        id: "f8",
        q: "How do I troubleshoot user login issues?",
        a: "Check the Users section to verify the user's account status, email address, and permissions. You can reset passwords or resend activation emails from the user detail page.",
      },
    ],
    []
  );

  // ----- Search + accordion state -----
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allFaqs;
    return allFaqs.filter(
      (f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q)
    );
  }, [allFaqs, query]);

  const toggle = (id) => setOpenId((prev) => (prev === id ? null : id));

  return (
    <div className="ahelp-container">
      {/* Breadcrumb */}
      <button className="welcome">� Back Admin Dashboard</button>

      {/* Page title */}
      <h1 className="username">Admin Help &amp; Support</h1>

      {/* Hero / search card */}
      <div className="ahelp-hero ahelp-card">
        <div className="ahelp-hero-inner">
          <h2 className="ahelp-hero-title">Need Assistance?</h2>
          <p className="ahelp-hero-subtitle">
            Search our admin knowledge base to find answers about platform
            management, user administration, and analytics.
          </p>

          <div className="ahelp-hero-search">
            <div className="ahelp-search-input-wrap">
              <svg
                className="ahelp-search-icon"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z"
                  stroke="#9CA3AF"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14 14L11.1 11.1"
                  stroke="#9CA3AF"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search"
                aria-label="Search admin help articles and FAQs"
              />
            </div>

            <button
              className="ahelp-submit-btn ahelp-search-btn"
              onClick={() => {
                // optional: hook this up to a backend search later
              }}
            >
              Search Now
              <svg
                className="ahelp-arrow-icon"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.33337 8H12.6667"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 3.33334L12.6667 8.00001L8 12.6667"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* FAQ list */}
      <div className="ahelp-card">
        <div className="ahelp-card-head">Frequently Asked Questions</div>

        <ul className="ahelp-faq-list" role="list">
          {filtered.map((item) => {
            const open = openId === item.id;
            return (
              <li key={item.id} className={`ahelp-faq-item ${open ? "open" : ""}`}>
                <button
                  className="ahelp-faq-toggle"
                  aria-expanded={open}
                  aria-controls={`ahelp-faq-panel-${item.id}`}
                  onClick={() => toggle(item.id)}
                >
                  <span className="ahelp-faq-q">{item.q}</span>
                  <span className="ahelp-faq-plus" aria-hidden>
                    {open ? (
                      // minus SVG
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M19.002 12.002H5" stroke="#141B34" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      // plus SVG
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5V19.002" stroke="#141B34" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M19.002 12.002H5" stroke="#141B34" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </span>
                </button>

                <div
                  id={`ahelp-faq-panel-${item.id}`}
                  role="region"
                  aria-labelledby={`ahelp-faq-btn-${item.id}`}
                  className="ahelp-faq-panel"
                >
                  <p className="ahelp-faq-a">{item.a}</p>
                </div>
              </li>
            );
          })}

          {filtered.length === 0 && (
            <li className="ahelp-faq-empty">
              No results for "{query}". Try a different keyword.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}
